import React, { useState } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { apiPostTOUVersionMaster, apiPutTOUVersionMaster } from "@app/api";
import { DataDialog } from "@app/components/atoms";
import {
  countryCodeOptions,
  ISOLocaleCode,
} from "@app/constants/route.constants";

const typeOptions = [
  { label: "TOU", value: "TOU" },
  { label: "Privacy Policy", value: "PRIVACY_POLICY" },
];

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  onSuccess?: () => void;
  initialData?: any;
}

const CreateTOUDialog: React.FC<Props> = ({
  open,
  setOpen,
  onSuccess,
  initialData,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<string>(initialData?.type || "");
  const [version, setVersion] = useState<string>(initialData?.version || "");
  const [pdfUrl, setPdfUrl] = useState<string>(initialData?.pdfUrl || "");
  const [residenceCountryCode, setResidenceCountryCode] = useState<string>(
    initialData?.residenceCountryCode || ""
  );
  const [isoLocaleCode, setIsoLocaleCode] = useState<string | null>(
    initialData?.isoLocaleCode ?? null
  );
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open && initialData) {
      setType(initialData.type || "");
      setVersion(initialData.version || "");
      setPdfUrl(initialData.pdfUrl || "");
      setResidenceCountryCode(initialData.residenceCountryCode || "");
      setIsoLocaleCode(initialData?.isoLocaleCode ?? null);
    } else if (open) {
      setType("");
      setVersion("");
      setPdfUrl("");
      setResidenceCountryCode("");
      setIsoLocaleCode(null);
    }
  }, [open, initialData]);

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload: any = {
        type,
        version,
        pdfUrl,
        residenceCountryCode,
        isoLocaleCode: isoLocaleCode || null,
      };
      if (initialData?.touVersionMasterId) {
        payload.touVersionMasterId = initialData.touVersionMasterId;
        await apiPutTOUVersionMaster(payload);
      } else {
        await apiPostTOUVersionMaster(payload);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error creating/updating TOU", err);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const isSaveDisabled =
    loading || !type || !version || !pdfUrl || !residenceCountryCode;

  const versionTypingRegex = /^\d[\d.]*$/;

  const isoLocaleOptions = [
    { value: "", label: t("selectIso") },
    ...Object.values(ISOLocaleCode).map((code) => ({
      value: code,
      label: code.toUpperCase(),
    })),
  ];

  return (
    <DataDialog
      disableBackdropClick
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      title={
        initialData ? t("formAction.edit") || t("createTOU") : t("createTOU")
      }
    >
      <DialogContent
        dividers
        sx={{
          maxHeight: "65vh",
          overflowY: "auto",
          pb: 10,
        }}
      >
        <TextField
          fullWidth
          label={t("license.countryCode")}
          value={residenceCountryCode}
          onChange={(e) => setResidenceCountryCode(e.target.value)}
          margin="normal"
          select
        >
          {countryCodeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label={t("subType")}
          value={type}
          onChange={(e) => setType(e.target.value)}
          margin="normal"
        >
          {typeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label={t("version")}
          value={version}
          margin="normal"
          className="input-field"
          inputProps={{
            inputMode: "decimal",
            pattern: "[0-9.]*",
          }}
          onChange={(e) => {
            const value = e.target.value;

            if (value === "" || versionTypingRegex.test(value)) {
              setVersion(value);
            }
          }}
        />

        <TextField
          fullWidth
          label={t("pdfUrl")}
          value={pdfUrl}
          onChange={(e) => setPdfUrl(e.target.value)}
          margin="normal"
          className="input-field"
        />

        <TextField
          select
          fullWidth
          label={t("isoLocaleCode")}
          value={isoLocaleCode ?? ""}
          onChange={(e) =>
            setIsoLocaleCode(e.target.value === "" ? null : e.target.value)
          }
          margin="normal"
        >
          {isoLocaleOptions.map((opt) => (
            <MenuItem key={opt.value || "empty"} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#fff",
          zIndex: 1,
          px: 3,
          py: 2,
        }}
      >
        <Button onClick={handleClose} className="btn btn-no-border">
          {t("dialogReport.cancel")}
        </Button>

        <Button
          onClick={handleSave}
          className="btn btn-primary"
          disabled={isSaveDisabled}
        >
          {t("formAction.save")}
        </Button>
      </DialogActions>
    </DataDialog>
  );
};

export default CreateTOUDialog;
